import { useMemo } from 'react';
import {
  Bar,
  BarChart as RBarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getAttribute } from '../../lib/attributes';
import { groupCount } from '../../lib/aggregate';
import type { ChartRenderProps } from '../../lib/charts';
import { scaleCount } from '../../lib/scaling';

export default function Histogram({ blob, indices, slots }: ChartRenderProps) {
  const key = slots.value;
  const attribute = key ? getAttribute(key) : undefined;

  const data = useMemo(() => {
    if (!attribute) return null;
    const values = attribute.values(blob);
    const maxValue = values.reduce((m, v) => Math.max(m, v.value), 0);
    const counts = groupCount(
      indices,
      (i) => attribute.accessor(blob, i),
      maxValue + 1,
    );
    const sampleSize = blob.data.length;
    return values.map((v) => ({
      name: v.label,
      value: scaleCount(counts[v.value], sampleSize),
    }));
  }, [blob, indices, attribute]);

  if (!attribute || !data) {
    return <p className="chart-card__empty">Pick an ordinal attribute.</p>;
  }
  if (indices.length === 0) {
    return <p className="chart-card__empty">No matching records.</p>;
  }

  return (
    <div className="chart-card__body">
      <ResponsiveContainer width="100%" height={220}>
        <RBarChart data={data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#232830" vertical={false} />
          <XAxis
            dataKey="name"
            tick={{ fill: '#8a9099', fontSize: 10 }}
            interval={0}
            angle={-30}
            height={60}
            textAnchor="end"
          />
          <YAxis
            tick={{ fill: '#8a9099', fontSize: 10 }}
            allowDecimals={false}
          />
          <Tooltip
            contentStyle={{
              background: '#14181d',
              border: '1px solid #232830',
              fontSize: 12,
              color: '#e6e7e9',
            }}
            cursor={{ fill: 'rgba(79, 140, 255, 0.08)' }}
          />
          <Bar dataKey="value" fill="#4f8cff" />
        </RBarChart>
      </ResponsiveContainer>
    </div>
  );
}
